import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { WARootState } from './store';
import { platformUrl } from '@/utils/platformUrl';

const BUILD_TIME_SERVICES_URL = import.meta.env.VITE_SERVICES_URL as string;

/**
 * Standard fetchBaseQuery for the main SPA. Used when the app is running in
 * its own domain (not embedded inside Salesforce).
 */
const _spaBaseQuery = fetchBaseQuery({
  baseUrl: BUILD_TIME_SERVICES_URL,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as WARootState;
    const token = state.appView.UserInfo?.Token;
    const currentRole = state.auth.currentRole;

    if (token) {
      headers.set('Authentication', token);
      if (currentRole) {
        headers.set('Currentrole', currentRole.toString().toLowerCase());
      } else {
        headers.set('Currentrole', 'investor');
      }
    }

    return headers;
  },
});

function mergeFetchArgsHeaders(
  target: Record<string, string>,
  init?: HeadersInit,
): void {
  if (init === undefined) return;
  new Headers(init).forEach((value, key) => {
    target[key] = value;
  });
}

function hasHeaderCaseInsensitive(
  headers: Record<string, string>,
  name: string,
): boolean {
  const lower = name.toLowerCase();
  return Object.keys(headers).some((key) => key.toLowerCase() === lower);
}

/**
 * Widget-safe base query that uses `fetch(url_string, init)` instead of
 * `fetch(Request)`. Salesforce Locker Service intercepts `Request` objects
 * and replaces the response body with the Lightning page HTML. Using a
 * plain URL string bypasses this behavior.
 */
async function _widgetBaseQuery(
  args: string | FetchArgs,
  api: { getState: () => unknown; signal: AbortSignal },
): Promise<
  | { data: unknown; error?: undefined }
  | { error: FetchBaseQueryError; data?: undefined }
> {
  const state = api.getState() as WARootState;
  const runtimeUrl = state.appView.WaInfo?.ApiUrl;
  const baseUrl = (runtimeUrl || BUILD_TIME_SERVICES_URL).replace(/\/+$/, '');

  const token = state.appView.UserInfo?.Token;
  const currentRole = state.auth.currentRole;

  let path: string;
  let method = 'GET';
  let body: BodyInit | undefined;
  const params = new URLSearchParams();

  if (typeof args === 'string') {
    path = args;
  } else {
    path = args.url;
    method = args.method?.toUpperCase() ?? 'GET';
    if (args.body) {
      body =
        args.body instanceof FormData
          ? args.body
          : typeof args.body === 'string'
            ? args.body
            : JSON.stringify(args.body);
    }
    if (args.params) {
      const p = args.params as Record<
        string,
        string | number | boolean | null | undefined
      >;
      for (const [k, v] of Object.entries(p)) {
        if (v !== undefined && v !== null) params.set(k, String(v));
      }
    }
  }

  const qs = params.toString();
  const pathNeededLeadingSlash =
    !path.startsWith('/') &&
    !path.startsWith('http://') &&
    !path.startsWith('https://');
  const pathWithSlash = pathNeededLeadingSlash ? `/${path}` : path;
  const fullUrl =
    baseUrl +
    pathWithSlash +
    (qs ? (pathWithSlash.includes('?') ? '&' : '?') + qs : '');

  const headers: Record<string, string> = {};
  if (typeof args !== 'string' && args.headers) {
    mergeFetchArgsHeaders(headers, args.headers as HeadersInit);
  }
  const responseHandler =
    typeof args !== 'string' ? args.responseHandler : undefined;

  if (
    !hasHeaderCaseInsensitive(headers, 'Accept') &&
    responseHandler === undefined
  ) {
    headers.Accept = 'application/json';
  }
  if (token) {
    headers['Authentication'] = token;
    headers['Currentrole'] = currentRole
      ? currentRole.toString().toLowerCase()
      : 'investor';
  }
  if (body && !(body instanceof FormData)) {
    if (!hasHeaderCaseInsensitive(headers, 'Content-Type')) {
      headers['Content-Type'] = 'application/json';
    }
  }

  const signal =
    (typeof args !== 'string' ? args.signal : undefined) ?? api.signal;

  try {
    const response = await fetch(fullUrl, {
      method,
      headers,
      body,
      credentials: 'same-origin',
      signal,
    });

    let data: unknown;
    if (typeof responseHandler === 'function') {
      data = await responseHandler(response);
    } else {
      const text = await response.text();
      if (!response.ok) {
        return {
          error: { status: response.status, data: text } as FetchBaseQueryError,
        };
      }

      try {
        return { data: JSON.parse(text) };
      } catch {
        return {
          error: {
            status: 'PARSING_ERROR',
            originalStatus: response.status,
            data: text,
            error: 'Response was not valid JSON',
          } as FetchBaseQueryError,
        };
      }
    }

    if (!response.ok) {
      return {
        error: { status: response.status, data } as FetchBaseQueryError,
      };
    }

    return { data };
  } catch (e) {
    if (typeof e === 'object' && e !== null) {
      return { error: widgetTransportError(e, signal) };
    }
    return { error: widgetTransportError(String(e), signal) };
  }
}

function widgetTransportError(
  error: object | string,
  signal: AbortSignal,
): FetchBaseQueryError {
  const name =
    typeof error === 'object' &&
    'name' in error &&
    typeof error.name === 'string'
      ? error.name
      : '';
  if (signal.aborted || name === 'AbortError') {
    return { status: 'CUSTOM_ERROR', error: 'Aborted' };
  }
  const message =
    typeof error === 'string'
      ? error
      : 'message' in error && typeof error.message === 'string'
        ? error.message
        : 'Failed to fetch';
  return { status: 'FETCH_ERROR', error: message };
}

/**
 * Services API base query used by all RTK Query data slices.
 *
 * Delegates to one of two implementations:
 * - **Widget context** (WIDGET is set within the npm scripts for build:widgets): Uses a plain
 *   `fetch(url_string)` call to avoid Salesforce Locker Service intercepting
 *   `Request` objects and replacing response bodies with Lightning page HTML.
 * - **SPA context** (normal app): Uses the standard `fetchBaseQuery` which
 *   works with Vite's dev proxy and behaves identically to the original.
 */
export const waServicesQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const isWidget: boolean = Boolean(import.meta.env.VITE_WIDGET);

  if (isWidget) {
    return _widgetBaseQuery(args, api);
  }

  return _spaBaseQuery(args, api, extraOptions);
};

const _platformRawQuery = fetchBaseQuery({
  baseUrl: platformUrl,
  credentials: 'include',
  prepareHeaders: (headers) => {
    // Required for CSRF protection. The Platform's ValidateCustomHeaderAttribute
    // rejects POSTs without this header, and CORS preflight ensures only trusted
    // origins can send it. See: Filters/ValidateCustomHeaderAttribute.cs
    headers.set('X-Requested-With', 'XMLHttpRequest');
    return headers;
  },
});

const SIGNED_OUT_PATH = /\/Account\/(Login|Logoff)\/?$/i;

export const isSignedOutRedirect = (response: Response | undefined) => {
  if (!response?.redirected) return false;
  try {
    return SIGNED_OUT_PATH.test(new URL(response.url).pathname);
  } catch {
    return false;
  }
};

export const waPlatformQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const result = await _platformRawQuery(args, api, extraOptions);
  if (isSignedOutRedirect(result.meta?.response)) {
    return {
      error: { status: 401, data: result.data },
      meta: result.meta,
    };
  }
  return result;
};

/**
 * Resolves Aggregator API base URL: runtime `WaInfo.AggregatorApiUrl` (legacy
 * waInfo) with build-time `VITE_AGGSERVICES_URL` fallback.
 */
export function resolveAggregatorApiUrl(state: WARootState): string {
  const runtime = state.appView.WaInfo?.AggregatorApiUrl?.trim();
  const buildTime = import.meta.env.VITE_AGGSERVICES_URL?.trim();
  const raw = runtime || buildTime || '';
  return raw.replace(/\/+$/, '');
}

/** Indirection so tests can stub host resolution without rewriting RTK slices. */
export const aggregatorHost = {
  resolveUrl: resolveAggregatorApiUrl,
};

function prepareAggregatorHeaders(
  headers: Headers,
  getState: () => unknown,
): Headers {
  const state = getState() as WARootState;
  const token = state.appView.UserInfo?.Token;
  const currentRole = state.auth.currentRole;

  if (token) {
    headers.set('Authentication', token);
    headers.set(
      'Currentrole',
      currentRole ? currentRole.toString().toLowerCase() : 'investor',
    );
  }

  headers.set('accept', 'application/json, text/javascript, */*; q=0.01');
  headers.set('content-type', 'application/json');
  return headers;
}

/**
 * Hoisted Aggregator fetch (no sticky baseUrl). Callers pass an absolute URL
 * after `resolveAggregatorApiUrl`.
 */
const _aggregatorRawQuery = fetchBaseQuery({
  prepareHeaders: (headers, { getState }) =>
    prepareAggregatorHeaders(headers, getState),
});

function withAggregatorBaseUrl(
  args: string | FetchArgs,
  baseUrl: string,
): string | FetchArgs {
  if (typeof args === 'string') {
    return `${baseUrl}${args.startsWith('/') ? args : `/${args}`}`;
  }
  const path = args.url;
  return {
    ...args,
    url: `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`,
  };
}

/**
 * Aggregator services base query. Runtime `WaInfo.AggregatorApiUrl` wins over
 * build-time `VITE_AGGSERVICES_URL`. Shared by firm Get Latest Balances and
 * aggregator credentials.
 */
export const aggregatorQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const state = api.getState() as WARootState;
  const baseUrl = aggregatorHost.resolveUrl(state);
  if (!baseUrl) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        data: { message: 'Aggregator API URL is not configured' },
        error: 'Aggregator API URL is not configured',
      },
    };
  }

  return _aggregatorRawQuery(
    withAggregatorBaseUrl(args, baseUrl),
    api,
    extraOptions,
  );
};
